import type { AbsenceReason, Attendance, RosterStatus, Rsvp } from '@ukinfo/shared';
import { NAMED_ABSENCE_REASONS } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDay } from '../lib/sports';
import { BorrowPanel } from './BorrowPanel';
import {
  ABSENCE_REASON_LABELS,
  ATTENDANCE_LABELS,
  RSVP_LABELS,
} from '../lib/format';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorBlock,
  Input,
  LoadingBlock,
  Select,
  Tabs,
  cn,
  useToast,
} from './ui';
import { TagList } from './TagPicker';
import { ResponseHistory } from './ResponseHistory';

export interface Participant {
  person_id: string;
  first_name: string;
  last_name: string;
  rsvp: Rsvp | null;
  /** Zusage nur aus der Standard-Rückmeldung der Mannschaft. */
  rsvp_auto?: boolean;
  attendance: Attendance | null;
  reason: AbsenceReason | null;
  note: string | null;
  response_requested: number;
  attendance_expected: number;
  roster_status: RosterStatus;
  /** Zusatzinfos aus dem Kader, z. B. "Schichtarbeit". */
  tags?: string[];
  /** Für diesen Termin aus einer anderen Mannschaft dazugeholt. */
  borrowed_from_team_id?: string | null;
  borrowed_from_team_name?: string | null;
  /** Eingetragene längere Abwesenheit am Tag des Termins. */
  absence?: { id: string; from: string; to: string; reason: AbsenceReason | null } | null;
}

/** Woher die Liste kommt: ein Training oder ein Spiel. */
export type AttendanceSource =
  | { kind: 'session'; sessionId: string }
  | { kind: 'match'; teamId: string; matchId: string };

function endpoint(source: AttendanceSource): string {
  return source.kind === 'session'
    ? `/api/sports/sessions/${source.sessionId}/participants`
    : `/api/sports/teams/${source.teamId}/matches/${source.matchId}/attendance`;
}

function confirmEndpoint(source: AttendanceSource, sessionId: string | null): string | null {
  if (source.kind === 'session') return `/api/sports/sessions/${source.sessionId}/confirmation`;
  return sessionId ? `/api/sports/sessions/${sessionId}/confirmation` : null;
}

function writeEndpoint(source: AttendanceSource): string {
  return source.kind === 'session'
    ? `/api/sports/sessions/${source.sessionId}/response`
    : `/api/sports/teams/${source.teamId}/matches/${source.matchId}/attendance`;
}

interface ResponseInput {
  personId?: string;
  rsvp?: Rsvp;
  attendance?: Attendance;
  reason?: AbsenceReason;
  note?: string | null;
}

const RSVP_TONE: Record<Rsvp, 'success' | 'warning' | 'neutral'> = {
  YES: 'success',
  NO: 'warning',
  MAYBE: 'neutral',
  PENDING: 'neutral',
};

export function AttendanceDialog({
  open,
  onClose,
  title,
  source,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  source: AttendanceSource;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [reopenReason, setReopenReason] = useState('');
  const [reiter, setReiter] = useState<'liste' | 'uebersicht'>('liste');
  const [aushilfenZeigen, setAushilfenZeigen] = useState(false);
  const [aktiverFilter, setAktiverFilterState] = useState<ListFilter>('ALLE');
  const [gerade, setGerade] = useState<Set<string>>(new Set());
  const [notizOffen, setNotizOffen] = useState<string | null>(null);
  const [dazuholenOffen, setDazuholenOffen] = useState(false);
  // Spielkader: wer beim Spiel erwartet wird. Nur bei Spielen.
  const [kaderAuswahl, setKaderAuswahl] = useState<Set<string> | null>(null);
  // Verlauf: letzte Änderungen aller oder aufgeklappt je Person.
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  const [personVerlauf, setPersonVerlauf] = useState<string | null>(null);
  const setFilter = (wert: ListFilter): void => {
    setAktiverFilterState(wert);
    setGerade(new Set());
  };

  const queryKey = ['attendance', endpoint(source)];
  // Ob jemand die Liste führen darf, entscheidet der Server anhand des
  // Bereichs dieser Mannschaft. Die Oberfläche rät nicht mit.
  const list = useQuery({
    queryKey,
    queryFn: () =>
      api.get<{
        canManage: boolean;
        showResponses?: boolean;
        canShowResponses?: boolean;
        canRecordAttendance: boolean;
        canConfirm: boolean;
        canReopen: boolean;
        allowMaybe?: boolean;
        kind?: 'TRAINING' | 'MATCH';
        seasonId?: string;
        lineupConfirmedAt?: string | null;
        confirmedAt: string | null;
        confirmedBy: string | null;
        sessionId?: string;
        participants: Participant[];
      }>(endpoint(source)),
    enabled: open,
  });

  const confirmUrl = confirmEndpoint(source, list.data?.sessionId ?? null);
  const sessionId = source.kind === 'session' ? source.sessionId : list.data?.sessionId ?? null;
  // Neuer Stand der Liste heißt meist auch neue Einträge im Verlauf.
  const listeStand = list.dataUpdatedAt;
  useEffect(() => {
    if (sessionId) void qc.invalidateQueries({ queryKey: ['response-history', sessionId] });
  }, [listeStand, sessionId, qc]);

  const zurueckgeben = useMutation({
    mutationFn: (personId: string) => api.del(`/api/sports/sessions/${sessionId}/borrowed/${personId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      await qc.invalidateQueries({ queryKey: ['sports-borrow-candidates'] });
      toast.show('Aus diesem Termin entfernt');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });
  const confirm = useMutation({
    mutationFn: (input: { confirmed: boolean; reason?: string }) => api.put(confirmUrl as string, input),
    onSuccess: async (_data, input) => {
      await qc.invalidateQueries({ queryKey });
      setReopenReason('');
      toast.show(input.confirmed ? 'Anwesenheit bestätigt' : 'Bestätigung aufgehoben');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });

  const sichtbarkeit = useMutation({
    mutationFn: (visible: boolean) =>
      api.put(`/api/sports/sessions/${sessionId}/responses-visible`, { visible }),
    onSuccess: async (_daten, visible) => {
      await qc.invalidateQueries({ queryKey });
      toast.show(visible ? 'Zu- und Absagen sind für alle sichtbar' : 'Nur noch die Mannschaftsleitung sieht die Liste');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });

  // Die Sammelfunktion des Servers ueberschreibt alle Erwarteten - auch schon
  // erfasste. Deshalb nur, solange noch niemand erfasst ist.
  const alle = useMutation({
    mutationFn: (input: { attendance: Attendance; anzahl: number }) =>
      api.put(`${(confirmUrl as string).replace('/confirmation', '')}/attendance-all`, { attendance: input.attendance }),
    onSuccess: async (_daten, input) => {
      await qc.invalidateQueries({ queryKey });
      toast.show(`${input.anzahl} ${input.anzahl === 1 ? 'Person' : 'Personen'} als anwesend übernommen`);
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });

  // Der Server kann nur alle setzen. Fuer "den Rest" gehen deshalb Einzelaufrufe
  // an genau die Personen ohne Erfassung - bereits Erfasste bleiben unberuehrt.
  const restlicheAnwesend = useMutation({
    mutationFn: async (personIds: string[]) => {
      const ergebnisse = await Promise.allSettled(personIds.map((personId) =>
        api.put(writeEndpoint(source), { personId, attendance: 'PRESENT' satisfies Attendance })));
      const gescheitert = ergebnisse.filter((e) => e.status === 'rejected');
      if (gescheitert.length > 0) {
        const grund = (gescheitert[0] as PromiseRejectedResult).reason;
        throw new Error(`${gescheitert.length} von ${personIds.length} konnten nicht übernommen werden${grund instanceof ApiError ? `: ${grund.message}` : ''}`);
      }
      return personIds.length;
    },
    onSuccess: async (anzahl) => {
      await qc.invalidateQueries({ queryKey });
      toast.show(`${anzahl} ${anzahl === 1 ? 'weitere Person' : 'weitere Personen'} als anwesend übernommen`);
    },
    onError: async (error: unknown) => {
      await qc.invalidateQueries({ queryKey });
      toast.show(error instanceof Error ? error.message : 'Nicht möglich', 'error');
    },
  });

  const save = useMutation({
    mutationFn: (input: ResponseInput) => {
      if (!list.data?.canManage && input.personId === user?.personId && source.kind === 'session') {
        return api.put(`/api/sports/sessions/${source.sessionId}/my-response`, {
          rsvp: input.rsvp, reason: input.reason, note: input.note,
        });
      }
      return api.put(writeEndpoint(source), input);
    },
    onMutate: (input) => {
      if (input.personId) setGerade((vorher) => new Set(vorher).add(input.personId as string));
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      toast.show('Gespeichert');
    },
    onError: (error: unknown) =>
      toast.show(
        error instanceof ApiError ? error.message : 'Konnte nicht gespeichert werden',
        'error',
      ),
  });

  const kaderSpeichern = useMutation({
    mutationFn: async (personIds: string[]) => {
      if (source.kind !== 'match') throw new Error('Nur bei Spielen');
      const url = `/api/sports/teams/${source.teamId}/matches/${source.matchId}/lineup`;
      // Trikotnummer und Position bleiben fuer alle erhalten, die im Kader bleiben.
      const bisher = await api.get<{ person_id: string; jersey_number: number | null; position: string; selection: 'STARTER' | 'BENCH' }[]>(url);
      const nachPerson = new Map(bisher.map((eintrag) => [eintrag.person_id, eintrag]));
      return api.put(url, {
        seasonId: list.data?.seasonId,
        players: personIds.map((personId) => {
          const alt = nachPerson.get(personId);
          return { personId, jerseyNumber: alt?.jersey_number ?? null, position: alt?.position ?? '', selection: alt?.selection ?? 'STARTER' };
        }),
      });
    },
    onSuccess: async (_data, personIds) => {
      await qc.invalidateQueries({ queryKey });
      setKaderAuswahl(null);
      toast.show(`Spielkader mit ${personIds.length} ${personIds.length === 1 ? 'Person' : 'Personen'} gespeichert`);
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Spielkader konnte nicht gespeichert werden', 'error'),
  });

  const alleZeilen = list.data?.participants ?? [];
  // Aushilfen und Inaktive koennen antworten, stehen aber nur auf Wunsch in
  // der Liste - sonst waere "offen" dauerhaft voll mit Namen, die niemand
  // erwartet. Die eigene Zeile bleibt immer sichtbar.
  const ausgeblendet = (row: Participant): boolean =>
    !aushilfenZeigen && row.roster_status === 'OCCASIONAL' && row.person_id !== user?.personId;
  const rows = alleZeilen.filter((row) => !ausgeblendet(row));
  const versteckt = alleZeilen.filter(ausgeblendet);
  const aushilfenGesamt = alleZeilen.filter((row) => row.roster_status === 'OCCASIONAL').length;
  const canManage = list.data?.canManage ?? false;
  const canRecordAttendance = list.data?.canRecordAttendance ?? false;
  const canConfirm = list.data?.canConfirm ?? false;
  const canReopen = list.data?.canReopen ?? false;
  const allowMaybe = list.data?.allowMaybe ?? false;
  const showResponses = list.data?.showResponses ?? false;
  const canShowResponses = list.data?.canShowResponses ?? false;
  const confirmedAt = list.data?.confirmedAt ?? null;
  const gesperrt = confirmedAt !== null;
  const istSpiel = list.data?.kind === 'MATCH';
  const kaderFestgelegt = !istSpiel || Boolean(list.data?.lineupConfirmedAt);
  const kaderModus = kaderAuswahl !== null;
  const kaderBearbeitbar = istSpiel && canManage && !gesperrt && source.kind === 'match';
  const zusagen = rows.filter((r) => r.rsvp === 'YES').length;
  const absagen = rows.filter((r) => r.rsvp === 'NO').length;
  const offen = rows.length - zusagen - absagen;
  const verletzt = rows.filter((r) => r.reason === 'INJURED').length;
  const erwartet = rows.filter((r) => r.attendance_expected === 1).length;
  const erwartetAlle = alleZeilen.filter((r) => r.attendance_expected === 1);
  const ohneErfassung = erwartetAlle.filter((r) => !r.attendance || r.attendance === 'UNKNOWN');
  const niemandErfasst = ohneErfassung.length === erwartetAlle.length;

  const kaderStarten = (): void => {
    // Vorschlag: bisheriger Kader, sonst alle Zusagen.
    setKaderAuswahl(new Set(alleZeilen
      .filter((row) => (kaderFestgelegt ? row.attendance_expected === 1 : row.rsvp === 'YES'))
      .map((row) => row.person_id)));
    setFilter('ALLE');
  };

  const noteFor = (row: Participant): string => noteDrafts[row.person_id] ?? row.note ?? '';

  // Filter je Status. Die Anwesenheit kommt dazu, sobald sie erfasst wird -
  // dann ist "wer fehlt noch?" die eigentliche Frage.
  const erfasst = rows.some((r) => r.attendance && r.attendance !== 'UNKNOWN');
  const filter: { id: ListFilter; label: string; anzahl: number; aktiv: string }[] = [
    { id: 'ALLE', label: 'alle', anzahl: rows.length, aktiv: 'bg-slate-800 text-white ring-slate-800' },
    { id: 'YES', label: 'zugesagt', anzahl: zusagen, aktiv: 'bg-emerald-600 text-white ring-emerald-600' },
    { id: 'MAYBE', label: 'vielleicht', anzahl: rows.filter((r) => r.rsvp === 'MAYBE').length, aktiv: 'bg-sky-600 text-white ring-sky-600' },
    { id: 'NO', label: 'abgesagt', anzahl: absagen, aktiv: 'bg-amber-600 text-white ring-amber-600' },
    { id: 'PENDING', label: 'offen', anzahl: rows.filter((r) => (r.rsvp ?? 'PENDING') === 'PENDING').length, aktiv: 'bg-slate-600 text-white ring-slate-600' },
    ...(canRecordAttendance || erfasst ? [
      { id: 'PRESENT' as const, label: 'anwesend', anzahl: rows.filter((r) => r.attendance === 'PRESENT').length, aktiv: 'bg-brand-600 text-white ring-brand-600' },
      { id: 'ABSENT' as const, label: 'abwesend', anzahl: rows.filter((r) => r.attendance === 'ABSENT' || r.attendance === 'EXCUSED').length, aktiv: 'bg-rose-600 text-white ring-rose-600' },
      { id: 'UNRECORDED' as const, label: 'nicht erfasst', anzahl: rows.filter((r) => r.attendance_expected === 1 && (!r.attendance || r.attendance === 'UNKNOWN')).length, aktiv: 'bg-slate-600 text-white ring-slate-600' },
    ] : []),
  ];
  const passt = (row: Participant): boolean => {
    switch (aktiverFilter) {
      case 'ALLE': return true;
      case 'PRESENT': return row.attendance === 'PRESENT';
      case 'ABSENT': return row.attendance === 'ABSENT' || row.attendance === 'EXCUSED';
      case 'UNRECORDED': return row.attendance_expected === 1 && (!row.attendance || row.attendance === 'UNKNOWN');
      default: return (row.rsvp ?? 'PENDING') === aktiverFilter;
    }
  };
  // Eine gerade geaenderte Zeile bleibt stehen, bis der Filter wechselt -
  // sonst verschwindet sie unter dem Finger, sobald man sie aendert.
  const sichtbar = rows.filter((row) => passt(row) || gerade.has(row.person_id));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      wide
      footer={<Button variant="secondary" onClick={onClose}>Schließen</Button>}
    >
      {list.isLoading && <LoadingBlock />}
      {/* Ein Ladefehler ist kein leerer Kader. */}
      {list.error && !list.isLoading && (
        <div className="space-y-2" role="alert">
          <ErrorBlock error={list.error} />
          <Button size="sm" variant="secondary" loading={list.isFetching} onClick={() => void list.refetch()}>Erneut laden</Button>
        </div>
      )}
      {!list.isLoading && !list.error && alleZeilen.length === 0 && (
        <EmptyState
          title="Kein Kader"
          description="Für diese Mannschaft und Saison sind keine Personen eingetragen."
        />
      )}

      {alleZeilen.length > 0 && (
        <>
          <Tabs
            tabs={[
              { id: 'liste', label: 'Liste' },
              { id: 'uebersicht', label: 'Übersicht' },
            ]}
            active={reiter}
            onChange={(id) => setReiter(id as 'liste' | 'uebersicht')}
          />

          <div className="mb-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {reiter === 'uebersicht' ? (
                <p className="flex flex-wrap gap-2 text-sm text-slate-600">
                  <Badge tone="success">{zusagen} zugesagt</Badge>
                  <Badge tone="warning">{absagen} abgesagt</Badge>
                  <Badge tone="neutral">{offen} offen</Badge>
                  {verletzt > 0 && <Badge tone="danger">{verletzt} verletzt</Badge>}
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Nach Status filtern">
                  {filter
                    // Die Grundfilter stehen immer da, damit die Leiste nicht springt;
                    // Vielleicht und Anwesenheit nur, wenn es etwas zu filtern gibt.
                    .filter((eintrag) => IMMER_SICHTBAR.includes(eintrag.id) || eintrag.anzahl > 0 || eintrag.id === aktiverFilter)
                    .map((eintrag) => (
                      <button
                        key={eintrag.id}
                        type="button"
                        aria-pressed={aktiverFilter === eintrag.id}
                        onClick={() => setFilter(aktiverFilter === eintrag.id ? 'ALLE' : eintrag.id)}
                        className={cn(
                          'rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                          aktiverFilter === eintrag.id ? eintrag.aktiv : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50',
                        )}
                      >
                        {eintrag.anzahl} {eintrag.label}
                      </button>
                    ))}
                </div>
              )}
              {aushilfenGesamt > 0 && (
                <Checkbox
                  label={`Aushilfen / Inaktive einblenden (${aushilfenGesamt})`}
                  checked={aushilfenZeigen}
                  onChange={setAushilfenZeigen}
                />
              )}
            </div>
            {versteckt.length > 0 && versteckt.some((row) => row.rsvp === 'YES') && (
              <p className="text-xs text-slate-500">
                Zusätzlich zugesagt (ausgeblendet):{' '}
                {versteckt
                  .filter((row) => row.rsvp === 'YES')
                  .map((row) => `${row.first_name} ${row.last_name}`)
                  .join(', ')}
              </p>
            )}

            {!canManage && showResponses && (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Für diesen Termin ist die Liste freigegeben: Du siehst, wer zu- und abgesagt hat. Gründe stehen nur
                bei deiner eigenen Zeile.
              </p>
            )}

            {gesperrt && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Bestätigt von {list.data?.confirmedBy ?? 'unbekannt'} am{' '}
                {new Date(confirmedAt as string).toLocaleString('de-AT')}. Zum Ändern zuerst die
                Bestätigung aufheben.
              </p>
            )}

            {reiter === 'liste' && canShowResponses && sessionId && !kaderModus && (
              <div className="rounded-lg bg-slate-50 px-3 py-2">
                <Checkbox
                  label="Zu- und Absagen für alle Beteiligten sichtbar"
                  checked={showResponses}
                  disabled={sichtbarkeit.isPending}
                  onChange={(wert) => sichtbarkeit.mutate(wert)}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Gilt nur für diesen Termin. Sichtbar wird, wer zu- oder abgesagt hat – der Grund einer Absage,
                  Notizen und die Anwesenheit bleiben bei der Mannschaftsleitung.
                </p>
              </div>
            )}

            {reiter === 'liste' && kaderBearbeitbar && !kaderModus && (
              <div className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm',
                kaderFestgelegt ? 'bg-slate-50 text-slate-600' : 'bg-amber-50 text-amber-900')}>
                <span>
                  {kaderFestgelegt
                    ? `Spielkader: ${erwartet} ${erwartet === 1 ? 'Person' : 'Personen'} – nur sie werden bei der Anwesenheit erwartet.`
                    : 'Zuerst den Spielkader festlegen. Danach lässt sich die Anwesenheit erfassen und bestätigen.'}
                </span>
                <Button size="sm" variant={kaderFestgelegt ? 'secondary' : 'primary'} onClick={kaderStarten}>
                  {kaderFestgelegt ? 'Spielkader ändern' : 'Spielkader festlegen'}
                </Button>
              </div>
            )}

            {reiter === 'liste' && kaderAuswahl !== null && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">
                <span className="mr-auto">Wer steht im Spielkader? {kaderAuswahl.size} ausgewählt</span>
                <Button size="sm" variant="ghost" onClick={() => setKaderAuswahl(new Set(alleZeilen.filter((row) => row.rsvp === 'YES').map((row) => row.person_id)))}>
                  Alle Zusagen
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setKaderAuswahl(null)}>Abbrechen</Button>
                <Button size="sm" loading={kaderSpeichern.isPending} onClick={() => kaderSpeichern.mutate([...kaderAuswahl])}>
                  Spielkader speichern
                </Button>
              </div>
            )}

            {reiter === 'liste' && canManage && confirmUrl && !kaderModus && (
              <div className="flex flex-wrap gap-2">
                {!gesperrt && (
                  <>
                    {/* Der Regelfall: fast alle sind da. Erst alle setzen,
                        dann die Ausnahmen korrigieren. */}
                    {canRecordAttendance && kaderFestgelegt && ohneErfassung.length > 0 && (
                      niemandErfasst ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={alle.isPending}
                          onClick={() => alle.mutate({ attendance: 'PRESENT', anzahl: erwartetAlle.length })}
                        >
                          {erwartetAlle.length} erwartete {erwartetAlle.length === 1 ? 'Person' : 'Personen'} als anwesend übernehmen
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={restlicheAnwesend.isPending}
                          onClick={() => restlicheAnwesend.mutate(ohneErfassung.map((r) => r.person_id))}
                        >
                          Restliche {ohneErfassung.length} als anwesend übernehmen
                        </Button>
                      )
                    )}
                    {canConfirm && (
                      <Button size="sm" loading={confirm.isPending} onClick={() => confirm.mutate({ confirmed: true })}>
                        Anwesenheit bestätigen
                      </Button>
                    )}
                    {sessionId && !dazuholenOffen && (
                      <Button size="sm" variant="ghost" onClick={() => setDazuholenOffen(true)}>
                        + Spieler dazuholen
                      </Button>
                    )}
                  </>
                )}
                {gesperrt && canReopen && (
                  <>
                    <Input
                      aria-label="Korrekturbegründung"
                      placeholder="Kurze Korrekturbegründung"
                      value={reopenReason}
                      onChange={(event) => setReopenReason(event.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={reopenReason.trim().length < 3}
                      loading={confirm.isPending}
                      onClick={() => confirm.mutate({ confirmed: false, reason: reopenReason.trim() })}
                    >
                      Bestätigung aufheben
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {reiter === 'uebersicht' && <Uebersicht rows={rows} currentPersonId={user?.personId} />}

          {reiter === 'liste' && dazuholenOffen && sessionId && canManage && !gesperrt && (
            <BorrowPanel sessionId={sessionId} onClose={() => setDazuholenOffen(false)} />
          )}

          {reiter === 'liste' && sichtbar.length === 0 && (
            <p className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
              Niemand mit diesem Status.{' '}
              <button type="button" className="text-brand-700 hover:underline" onClick={() => setFilter('ALLE')}>Alle zeigen</button>
            </p>
          )}

          {reiter === 'liste' && sessionId && (
            <div className="rounded-xl border border-slate-200 px-3 py-2">
              <button
                type="button"
                className="flex min-h-[32px] w-full items-center justify-between text-left text-sm font-medium text-slate-800"
                aria-expanded={verlaufOffen}
                onClick={() => setVerlaufOffen(!verlaufOffen)}
              >
                Letzte Änderungen
                <span aria-hidden="true" className="text-slate-400">{verlaufOffen ? '▴' : '▾'}</span>
              </button>
              {verlaufOffen && <div className="pb-1 pt-1"><ResponseHistory sessionId={sessionId} limit={10} /></div>}
            </div>
          )}

          {reiter === 'liste' && sichtbar.length > 0 && (
          <>
          {/* Ueberschrift ueber den Knoepfen: Rueckmeldung ist die Ansage vorher,
              Anwesenheit das, was tatsaechlich war. */}
          <p className="flex justify-end gap-6 px-3 text-xs font-medium uppercase tracking-wide text-slate-500">
            <span>Rückmeldung</span>
            {canRecordAttendance && <span>Anwesenheit</span>}
          </p>
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {sichtbar.map((row) => {
              const eigeneZeile = row.person_id === user?.personId;
              const name = `${row.first_name} ${row.last_name}`;
              const abgesagt = row.rsvp === 'NO' || row.attendance === 'ABSENT' || row.attendance === 'EXCUSED';
              const bearbeitbar = !gesperrt && (canManage || eigeneZeile);
              const mitAnwesenheit = canRecordAttendance && row.attendance_expected === 1;
              const rsvp = row.rsvp ?? 'PENDING';
              return (
                <li key={row.person_id} className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <div className="min-w-0 flex-1 basis-40">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {sessionId ? (
                          <button
                            type="button"
                            className="text-left hover:underline"
                            aria-expanded={personVerlauf === row.person_id}
                            title="Verlauf der Rückmeldungen"
                            onClick={() => setPersonVerlauf(personVerlauf === row.person_id ? null : row.person_id)}
                          >
                            {name}
                          </button>
                        ) : name}
                        {eigeneZeile && <span className="ml-1 text-xs font-normal text-slate-500">(ich)</span>}
                        {row.roster_status === 'OCCASIONAL' && <span className="ml-1 text-xs font-normal text-slate-500">· Aushilfe</span>}
                        {row.tags && row.tags.length > 0 && <TagList tags={row.tags} className="ml-1.5 align-middle" />}
                        {row.borrowed_from_team_id && (
                          <span className="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700">von {row.borrowed_from_team_name ?? 'anderer Mannschaft'}</span>
                        )}
                        {row.borrowed_from_team_id && canManage && !gesperrt && (
                          <button
                            type="button"
                            className="ml-2 text-xs font-normal text-rose-700 hover:underline"
                            disabled={zurueckgeben.isPending}
                            aria-label={`${name} aus diesem Termin entfernen`}
                            onClick={() => { if (window.confirm(`${name} aus diesem Termin entfernen?`)) zurueckgeben.mutate(row.person_id); }}
                          >
                            entfernen
                          </button>
                        )}
                      </p>
                      {row.rsvp_auto && !row.absence && (
                        <p className="truncate text-xs text-slate-400">automatisch zugesagt</p>
                      )}
                      {(row.absence || (!bearbeitbar && abgesagt && row.reason && row.reason !== 'NONE')) && (
                        <p className="truncate text-xs text-amber-700">
                          {row.absence
                            ? `Abwesend ${formatDay(row.absence.from)} – ${formatDay(row.absence.to)}${row.rsvp === 'YES' ? ' · trotzdem zugesagt' : ''}`
                            : ABSENCE_REASON_LABELS[row.reason as AbsenceReason]}
                        </p>
                      )}
                    </div>

                    {kaderAuswahl !== null ? (
                      <>
                        <Badge tone={RSVP_TONE[rsvp]}>{RSVP_LABELS[rsvp]}</Badge>
                        <Checkbox
                          label="im Spielkader"
                          checked={kaderAuswahl.has(row.person_id)}
                          onChange={(wert) => setKaderAuswahl((vorher) => {
                            const naechste = new Set(vorher);
                            if (wert) naechste.add(row.person_id); else naechste.delete(row.person_id);
                            return naechste;
                          })}
                        />
                      </>
                    ) : bearbeitbar ? (
                      <Segmente
                        label={`Rückmeldung ${name}`}
                        value={rsvp}
                        // "Vielleicht" nur, wenn es fuer den Termin erlaubt ist -
                        // oder die Person es schon gewaehlt hat.
                        options={allowMaybe || rsvp === 'MAYBE' ? RSVP_OPTIONEN : RSVP_OPTIONEN.filter((o) => o.value !== 'MAYBE')}
                        onChange={(wert) => save.mutate({ personId: row.person_id, rsvp: wert })}
                      />
                    ) : (
                      <Badge tone={RSVP_TONE[rsvp]}>{RSVP_LABELS[rsvp]}</Badge>
                    )}

                    {/* Die Anwesenheit stellt nur fest, wer die Liste fuehrt. */}
                    {kaderModus ? null : bearbeitbar && mitAnwesenheit ? (
                      <Segmente
                        label={`Anwesenheit ${name}`}
                        value={row.attendance === 'EXCUSED' ? 'ABSENT' : row.attendance ?? 'UNKNOWN'}
                        options={ANWESENHEIT_OPTIONEN}
                        onChange={(wert) => save.mutate({ personId: row.person_id, attendance: wert })}
                      />
                    ) : (
                      row.attendance && row.attendance !== 'UNKNOWN' && <Badge tone="info">{ATTENDANCE_LABELS[row.attendance]}</Badge>
                    )}
                  </div>

                  {bearbeitbar && !kaderModus && (abgesagt || rsvp === 'MAYBE') && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 sm:pl-0">
                      {/* "Vielleicht" hat keinen Grund, nur eine Notiz. */}
                      {abgesagt && (
                        <Select
                          aria-label={`Grund ${name}`}
                          value={row.reason ?? 'NONE'}
                          className="h-8 w-auto rounded-lg py-0 pl-2.5 text-sm"
                          onChange={(e) => save.mutate({ personId: row.person_id, reason: e.target.value as AbsenceReason })}
                        >
                          <option value="NONE">Grund …</option>
                          {NAMED_ABSENCE_REASONS.map((value) => (
                            <option key={value} value={value}>{ABSENCE_REASON_LABELS[value]}</option>
                          ))}
                        </Select>
                      )}
                      {notizOffen === row.person_id ? (
                        <Input
                          autoFocus
                          aria-label={`Notiz ${name}`}
                          placeholder={abgesagt ? 'Notiz (z. B. voraussichtlich 4 Wochen)' : 'Notiz (z. B. komme später)'}
                          className="h-8 min-w-0 flex-1 rounded-lg py-0 text-sm"
                          value={noteFor(row)}
                          onChange={(e) => setNoteDrafts({ ...noteDrafts, [row.person_id]: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          onBlur={() => {
                            setNotizOffen(null);
                            const wert = noteFor(row).trim();
                            if (wert === (row.note ?? '')) return;
                            save.mutate({ personId: row.person_id, note: wert || null });
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="min-w-0 truncate text-left text-xs text-slate-500 hover:text-brand-700"
                          onClick={() => setNotizOffen(row.person_id)}
                        >
                          {row.note ? `„${row.note}“ ✎` : '+ Notiz'}
                        </button>
                      )}
                    </div>
                  )}
                  {!bearbeitbar && row.note && <p className="mt-0.5 truncate text-xs text-slate-500">{row.note}</p>}
                  {sessionId && personVerlauf === row.person_id && (
                    <div className="mt-1.5 rounded-lg bg-slate-50 px-2 py-1.5">
                      <ResponseHistory sessionId={sessionId} personId={row.person_id} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          </>
          )}
        </>
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------ Kompakte Auswahl */

type ListFilter = Rsvp | 'ALLE' | 'PRESENT' | 'ABSENT' | 'UNRECORDED';

const IMMER_SICHTBAR: readonly ListFilter[] = ['ALLE', 'YES', 'NO', 'PENDING'];

interface Segment<T extends string> { value: T; kurz: string; titel: string; aktiv: string }

const RSVP_OPTIONEN: Segment<Rsvp>[] = [
  { value: 'YES', kurz: '✓', titel: 'Zugesagt', aktiv: 'bg-emerald-600 text-white' },
  { value: 'MAYBE', kurz: '?', titel: 'Vielleicht', aktiv: 'bg-sky-600 text-white' },
  { value: 'NO', kurz: '✕', titel: 'Abgesagt', aktiv: 'bg-amber-600 text-white' },
  // Ruhezustand hell - ein dunkles Feld sieht aus wie eine Auswahl.
  { value: 'PENDING', kurz: '–', titel: 'Offen', aktiv: 'bg-slate-100 text-slate-600' },
];

const ANWESENHEIT_OPTIONEN: Segment<Attendance>[] = [
  { value: 'PRESENT', kurz: 'Da', titel: 'Anwesend', aktiv: 'bg-brand-600 text-white' },
  { value: 'ABSENT', kurz: 'Fehlt', titel: 'Abwesend', aktiv: 'bg-rose-600 text-white' },
  { value: 'UNKNOWN', kurz: '–', titel: 'Nicht erfasst', aktiv: 'bg-slate-100 text-slate-600' },
];

/**
 * Knopfgruppe statt Auswahlliste: ein Klick statt zwei, und der Stand ist
 * ohne Aufklappen lesbar. Die vollen Bezeichnungen stehen als Tooltip und
 * fuer Screenreader bereit.
 */
export function Segmente<T extends string>({
  label, value, options, onChange,
}: {
  label: string;
  value: T;
  options: Segment<T>[];
  onChange: (wert: T) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label={label} className="inline-flex shrink-0 overflow-hidden rounded-lg ring-1 ring-inset ring-slate-300">
      {options.map((option) => {
        const aktiv = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            title={option.titel}
            aria-label={option.titel}
            aria-pressed={aktiv}
            onClick={() => { if (!aktiv) onChange(option.value); }}
            className={cn(
              'h-8 min-w-[2.25rem] border-l border-slate-200 px-2 text-sm font-medium first:border-l-0 transition-colors',
              aktiv ? option.aktiv : 'bg-white text-slate-500 hover:bg-slate-50',
            )}
          >
            {option.kurz}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- Übersicht */

const GRUPPEN: { rsvp: Rsvp; titel: string; kopf: string; balken: string }[] = [
  { rsvp: 'YES', titel: 'Zusagen', kopf: 'bg-emerald-50 text-emerald-800 border-emerald-200', balken: 'bg-emerald-500' },
  { rsvp: 'MAYBE', titel: 'Vielleicht', kopf: 'bg-sky-50 text-sky-800 border-sky-200', balken: 'bg-sky-400' },
  { rsvp: 'NO', titel: 'Absagen', kopf: 'bg-amber-50 text-amber-800 border-amber-200', balken: 'bg-amber-500' },
  { rsvp: 'PENDING', titel: 'Offen', kopf: 'bg-slate-50 text-slate-700 border-slate-200', balken: 'bg-slate-300' },
];

/**
 * Auf einen Blick: wer kommt, wer nicht, wem man noch nachlaufen muss.
 *
 * Nur lesen, nichts bearbeiten - dafuer ist die Liste da. Namen stehen
 * nebeneinander statt in Karten, damit auch ein voller Kader ohne
 * Scrollen sichtbar bleibt.
 */
export function Uebersicht({
  rows,
  currentPersonId,
}: {
  rows: Participant[];
  currentPersonId: string | undefined;
}): JSX.Element {
  const gruppen = GRUPPEN.map((gruppe) => ({
    ...gruppe,
    personen: rows
      .filter((row) => (row.rsvp ?? 'PENDING') === gruppe.rsvp)
      .sort((a, b) => a.last_name.localeCompare(b.last_name, 'de-AT')),
  })).filter((gruppe) => gruppe.rsvp !== 'MAYBE' || gruppe.personen.length > 0);
  const gesamt = rows.length;

  return (
    <div className="space-y-3">
      {gesamt > 0 && (
        <div
          className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"
          role="img"
          aria-label={gruppen.map((g) => `${g.personen.length} ${g.titel}`).join(', ')}
        >
          {gruppen.map((gruppe) =>
            gruppe.personen.length > 0 ? (
              <div
                key={gruppe.rsvp}
                className={gruppe.balken}
                style={{ width: `${(gruppe.personen.length / gesamt) * 100}%` }}
              />
            ) : null,
          )}
        </div>
      )}

      <div className={cn('grid gap-3', gruppen.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3')}>
        {gruppen.map((gruppe) => (
          <section key={gruppe.rsvp} className="overflow-hidden rounded-xl border border-slate-200">
            <h3 className={cn('flex items-baseline justify-between border-b px-3 py-2 text-sm font-semibold', gruppe.kopf)}>
              <span>{gruppe.titel}</span>
              <span className="text-lg tabular-nums">{gruppe.personen.length}</span>
            </h3>
            {gruppe.personen.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">Niemand</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {gruppe.personen.map((row) => (
                  <li key={row.person_id} className="px-3 py-1.5">
                    <span className={cn(row.person_id === currentPersonId && 'font-semibold')}>
                      {row.first_name} {row.last_name}
                    </span>
                    {row.roster_status === 'OCCASIONAL' && (
                      <span className="ml-1 text-xs text-slate-500">· Aushilfe</span>
                    )}
                    {row.rsvp_auto && <span className="ml-1 text-xs text-slate-400">· automatisch</span>}
                    {gruppe.rsvp === 'NO' && row.reason && row.reason !== 'NONE' && (
                      <span className={cn('block text-xs', row.reason === 'INJURED' ? 'text-rose-700' : 'text-slate-500')}>
                        {ABSENCE_REASON_LABELS[row.reason]}
                        {row.absence ? ` bis ${formatDay(row.absence.to)}` : ''}
                        {row.note ? ` · ${row.note}` : ''}
                      </span>
                    )}
                    {gruppe.rsvp === 'MAYBE' && row.note && (
                      <span className="block text-xs text-slate-500">{row.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
